/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { TransformStatus } from "./QuickFormTypes";
import { ThemeType } from "../DesignSystem/Theme/Theme";

interface TransformStatusIconProps {
  status: TransformStatus;
  theme: ThemeType;
  errorMessage?: string;
  onErrorClick?: () => void;
}

export const TransformStatusIcon: React.FC<TransformStatusIconProps> = ({
  status,
  theme,
  errorMessage,
  onErrorClick,
}) => {
  const getStatusStyles = (): {
    background: string;
    borderColor: string;
    iconColor: string;
    label: string;
  } => {
    switch (status) {
      case TransformStatus.SUCCESS:
        return {
          background: `rgba(104, 211, 145, 0.08)`,
          borderColor: `rgba(104, 211, 145, 0.3)`,
          iconColor: theme.colors.positive,
          label: "Transformed",
        };
      case TransformStatus.FAILED:
        return {
          background: `rgba(239, 68, 68, 0.08)`,
          borderColor: `rgba(239, 68, 68, 0.3)`,
          iconColor: theme.colors.negative,
          label: "Failed",
        };
      case TransformStatus.PROCESSING:
        return {
          background: `rgba(139, 233, 253, 0.08)`,
          borderColor: `rgba(139, 233, 253, 0.3)`,
          iconColor: theme.colors.secondary,
          label: "Processing...",
        };
      case TransformStatus.PENDING:
      default:
        return {
          background: `rgba(90, 90, 105, 0.2)`,
          borderColor: theme.colors.dropdownBorderColor,
          iconColor: theme.colors.secondaryTextColor,
          label: "Pending",
        };
    }
  };

  const styles = getStatusStyles();

  const renderIcon = (): JSX.Element => {
    switch (status) {
      case TransformStatus.SUCCESS:
        return (
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke={styles.iconColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="8" cy="8" r="6.5" />
            <polyline points="5,8.2 7.2,10.5 11,6" />
          </svg>
        );
      case TransformStatus.FAILED:
        return (
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke={styles.iconColor}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8 2L14.5 13.5H1.5L8 2Z" />
            <line x1="8" y1="7" x2="8" y2="10" />
            <circle
              cx="8"
              cy="12"
              r="0.6"
              fill={styles.iconColor}
              stroke="none"
            />
          </svg>
        );
      case TransformStatus.PROCESSING:
        return (
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke={styles.iconColor}
            strokeWidth="2"
            strokeLinecap="round"
            style={{
              animation: "spin 1.2s linear infinite",
            }}
          >
            <path d="M8 1.5A6.5 6.5 0 1 1 1.5 8" />
          </svg>
        );
      case TransformStatus.PENDING:
      default:
        return (
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke={styles.iconColor}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="8" cy="8" r="6.5" />
            <polyline points="8,4.5 8,8 10.5,10" />
          </svg>
        );
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          padding: "5px 10px",
          borderRadius: "5px",
          fontSize: theme.fontSizes.small,
          fontWeight: 500,
          background: styles.background,
          border: `1px solid ${styles.borderColor}`,
          color: theme.colors.text,
          minWidth: "120px",
        }}
      >
        <div
          style={{
            width: "18px",
            height: "18px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {renderIcon()}
        </div>
        {styles.label}
      </div>
      {status === TransformStatus.FAILED && errorMessage && onErrorClick && (
        <div
          style={{
            fontSize: "13px",
            color: theme.colors.linkColor,
            cursor: "pointer",
            textDecoration: "underline",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
          onClick={onErrorClick}
        >
          <FaExternalLinkAlt size={12} />
          View Error Details
        </div>
      )}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
